import { useState } from 'react';
import { ehIPad, ehIOS } from '@/lib/app';
import Sheet from '@/components/ui/Sheet';
import PrimaryButton from '@/components/ui/PrimaryButton';

interface FolhaColocarNaTelaProps {
  aoFechar: () => void;
  descricao?: string;
  fraseDestaque?: string;
}

// Folha "Colocar o Se Liga na tela" — passo a passo ilustrado do Safari no
// iPhone/iPad: 3 passos, bolinhas de progresso e botão "Próximo". Seta animada
// aponta o botão Compartilhar (iPhone: barra de baixo, no meio; iPad: canto de
// cima, à direita).
export default function FolhaColocarNaTela({
  aoFechar,
  descricao,
  fraseDestaque,
}: FolhaColocarNaTelaProps) {
  const [passo, setPasso] = useState(0);
  const ehTablet = ehIPad();

  const passos = [
    {
      icone: 'ri-share-forward-box-line',
      titulo: 'Toque em Compartilhar',
      texto: ehTablet
        ? 'É o quadradinho com a seta para cima, no canto de cima da tela.'
        : 'É o quadradinho com a seta para cima, na barra de baixo do Safari.',
    },
    {
      icone: 'ri-add-box-line',
      titulo: 'Toque em “Adicionar à Tela de Início”',
      texto: 'Role a lista até achar. Se aparecer “Abrir como app web”, deixe ligado.',
    },
    {
      icone: 'ri-check-line',
      titulo: 'Toque em Adicionar',
      texto: 'Pronto! Agora abra o Se Liga pelo ícone novo, na tela de início do celular.',
    },
  ];

  const atual = passos[passo];
  const ultimo = passo === passos.length - 1;

  return (
    <Sheet aberto aoFechar={aoFechar} zIndex={98}>
      <div className="flex flex-col items-center pt-2 text-center">
        <span className="flex h-16 w-16 items-center justify-center text-primary-500">
          <i className="ri-smartphone-line text-[44px]" aria-hidden />
        </span>
        <h2 className="mt-3 text-[20px] font-extrabold leading-tight text-foreground-950">
          Colocar o Se Liga na tela
        </h2>
        {descricao && (
          <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-foreground-600">
            {descricao}
          </p>
        )}
        {fraseDestaque && (
          <p className="mt-3 rounded-[14px] bg-warning-50 px-3 py-2 text-[13px] leading-snug text-warning-800">
            {fraseDestaque}
          </p>
        )}
      </div>

      {/* Bolinhas de progresso. */}
      <div className="mt-5 flex items-center justify-center gap-2">
        {passos.map((_, i) => (
          <span
            key={i}
            className={`h-2 rounded-full transition-all duration-200 ${
              i === passo ? 'w-6 bg-primary-500' : 'w-2 bg-background-300'
            }`}
          />
        ))}
      </div>

      <div className="mt-4 flex flex-col items-center text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600">
          <i className={`${atual.icone} text-2xl`} aria-hidden />
        </span>
        <p className="mt-3 text-[17px] font-extrabold text-foreground-950">{atual.titulo}</p>
        <p className="mt-1 max-w-sm text-[15px] leading-relaxed text-foreground-600">
          {atual.texto}
        </p>
      </div>

      {passo === 0 && ehIOS && (
        <div className="mt-4 flex items-center justify-center gap-2 text-primary-500">
          <span className="animate-seta-guia flex h-6 w-6 items-center justify-center">
            <i
              className={ehTablet ? 'ri-arrow-up-line text-2xl' : 'ri-arrow-down-line text-2xl'}
              aria-hidden
            />
          </span>
          <span className="text-xs font-medium text-foreground-500">
            {ehTablet ? 'Compartilhar fica lá em cima' : 'Compartilhar fica aqui embaixo'}
          </span>
        </div>
      )}

      <PrimaryButton
        cheio
        className="mt-6"
        icone={ultimo ? 'ri-check-line' : undefined}
        onClick={() => (ultimo ? aoFechar() : setPasso((p) => p + 1))}
      >
        {ultimo ? 'Entendi' : 'Próximo'}
      </PrimaryButton>

      {passo > 0 && (
        <button
          type="button"
          onClick={() => setPasso((p) => p - 1)}
          className="aperta mx-auto mt-2 block cursor-pointer whitespace-nowrap py-2 text-[15px] font-semibold text-foreground-500"
        >
          Voltar
        </button>
      )}
      <button
        type="button"
        onClick={aoFechar}
        className="aperta mx-auto mt-1 block cursor-pointer whitespace-nowrap py-2 text-[15px] font-semibold text-foreground-400 transition-colors hover:text-foreground-600"
      >
        Agora não
      </button>
    </Sheet>
  );
}